import { createFileRoute, notFound } from "@tanstack/react-router";
import { MemoryBrowsePage } from "@/components/memory/memory-browse-page";
import { collectionRouteValue } from "@/lib/collection-selection";
import { isReservedOwnerSegment } from "@/lib/reserved";
import { decodeEntityTypeParam } from "@/lib/url";

/**
 * First-class entity-type route: `/{owner}/<type>` renders the Records tab in
 * the schema's default shape. A pinned shape (`/-/table`, `/-/board`) and each
 * attached view (`/-/views/<key>`) are sibling paths, so this route owns its
 * query outright: `create`, and `segment` (one of the type's `on: "entity"`
 * segments narrowing the list). Individual entities resolve through the
 * `$owner/$` catch-all (`/{owner}/<type>/<slug>`).
 */
type EntityTypeSearchParams = {
	create?: boolean;
	segment?: string;
	$collection?: string;
	$preset?: string;
	$table?: string;
};
export const Route = createFileRoute("/$owner/$entityType/")({
	validateSearch: (
		search: Record<string, unknown>,
	): EntityTypeSearchParams => ({
		$collection: collectionRouteValue(search.$collection),
		$preset: collectionRouteValue(search.$preset),
		$table: collectionRouteValue(search.$table),
		create:
			search.create === true || search.create === "true" ? true : undefined,
		segment:
			typeof search.segment === "string" && search.segment
				? search.segment
				: undefined,
	}),
	beforeLoad: ({ params }) => {
		if (isReservedOwnerSegment(params.entityType)) {
			throw notFound();
		}
	},
	component: EntityTypeListPage,
});

function EntityTypeListPage() {
	const { owner, entityType } = Route.useParams();
	const { create } = Route.useSearch();
	return (
		<MemoryBrowsePage
			ownerSlug={owner}
			typeScope={decodeEntityTypeParam(entityType)}
			create={create ?? false}
		/>
	);
}
